import { create } from 'zustand';
import { DocumentContext, TDraft, toggleHeadingHelper, TVersion } from '@modules/documents';

type TImpactSourceStore = {
  selectedHeadingIds: ReadonlySet<string>;
  autoDisabledIds: ReadonlySet<string>;

  /**
   * Toggles the subtree of `headingId`. If `headingId` is already selected,
   * remove it (and children) from selectedHeadingIds. If it is not,
   * add it (and children) to selectedHeadingIds.
   */
  toggleHeading: (headingId: string, version: TDraft | TVersion) => void;

  /**
   * Directly set the entire selection from an external source,
   * e.g. the heading selection dialog’s final user picks.
   */
  setSelection: (selected: ReadonlySet<string>, autoDisabled: ReadonlySet<string>) => void;

  /** Clear all selections (e.g., on mount). */
  clearSelection: () => void;
};

const useImpactSourceStore = create<TImpactSourceStore>((set, get) => ({
  selectedHeadingIds: new Set<string>(),
  autoDisabledIds: new Set<string>(),

  toggleHeading: (headingId, version) => {
    set((state) => {
      const [newSelected, newAutoDisabled] = toggleHeadingHelper(
        version,
        headingId,
        state.selectedHeadingIds,
        state.autoDisabledIds,
        true // `enableAutoChildrenSelection` always true in this context
      );
      return {
        selectedHeadingIds: newSelected,
        autoDisabledIds: newAutoDisabled,
      };
    });
  },

  setSelection: (selected, autoDisabled) =>
    set(() => ({ selectedHeadingIds: selected, autoDisabledIds: autoDisabled })),

  clearSelection: () =>
    set(() => ({
      selectedHeadingIds: new Set(),
      autoDisabledIds: new Set(),
    })),
}));

export default useImpactSourceStore;
